import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { doc, getDoc, updateDoc, addDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

const STATUS_COLORS: Record<string, string> = { Open: '#dc2626', Reviewed: '#2563eb', Closed: '#16a34a' }
const SEVERITY_COLORS: Record<string, string> = { Low: '#3b82f6', Medium: '#f59e0b', High: '#f97316', Critical: '#dc2626' }
const STATUS_ORDER = ['Open', 'Reviewed', 'Closed']

interface Comment {
  id: string
  author: string
  authorId: string
  text: string
  createdAt: string
}

export default function NearMissDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { orgId, userData } = useAuth()
  const navigate = useNavigate()

  const [item, setItem] = useState<any>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [addingComment, setAddingComment] = useState(false)
  const [showReviewNotes, setShowReviewNotes] = useState(false)
  const [showCloseNotes, setShowCloseNotes] = useState(false)
  const [reviewNotes, setReviewNotes] = useState('')
  const [closeNotes, setCloseNotes] = useState('')
  const [statusUpdating, setStatusUpdating] = useState(false)

  useEffect(() => {
    if (!id || !orgId) return
    const q = query(
      collection(db, 'organizations', orgId, 'nearMiss', id, 'comments'),
      orderBy('createdAt', 'asc')
    )
    const unsub = onSnapshot(q, snap => {
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() } as Comment)))
    })
    return () => unsub()
  }, [id, orgId])

  useEffect(() => {
    if (!id || !orgId) return
    const load = async () => {
      const snap = await getDoc(doc(db, 'organizations', orgId, 'nearMiss', id))
      if (snap.exists()) {
        setItem({ id: snap.id, ...snap.data() })
      }
      setLoading(false)
    }
    load()
  }, [id, orgId])

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }) : ''

  const canTransitionTo = (target: string): boolean => {
    if (!item) return false
    const currentIdx = STATUS_ORDER.indexOf(item.status)
    const targetIdx = STATUS_ORDER.indexOf(target)
    return targetIdx === currentIdx + 1
  }

  const handleStatusUpdate = async (newStatus: string) => {
    if (!item || !orgId || !id) return
    if (!canTransitionTo(newStatus)) return

    let notes = ''
    if (newStatus === 'Reviewed') {
      if (!reviewNotes.trim()) {
        alert('Please enter review notes before marking as Reviewed.')
        return
      }
      notes = reviewNotes
    } else if (newStatus === 'Closed') {
      if (!closeNotes.trim()) {
        alert('Please enter close out notes before marking as Closed.')
        return
      }
      notes = closeNotes
    }

    setStatusUpdating(true)
    try {
      await updateDoc(doc(db, 'organizations', orgId, 'nearMiss', id), {
        status: newStatus,
        notes: notes || item.notes || '',
        updatedAt: new Date().toISOString()
      })

      await addDoc(collection(db, 'organizations', orgId, 'activityFeed'), {
        orgId,
        type: 'nearmiss_status_changed',
        nearMissId: id,
        nearMissTitle: item.title,
        userId: userData?.uid || 'unknown',
        userName: userData?.name || 'Unknown',
        message: `Near Miss/Good Catch marked ${newStatus} by ${userData?.name || 'Unknown'}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      const updated = await getDoc(doc(db, 'organizations', orgId, 'nearMiss', id))
      setItem({ id: updated.id, ...updated.data() })
      setShowReviewNotes(false)
      setShowCloseNotes(false)
      setReviewNotes('')
      setCloseNotes('')
    } catch (err: any) {
      alert(err?.message || 'Failed to update status.')
    } finally {
      setStatusUpdating(false)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !orgId || !id) return
    setAddingComment(true)
    try {
      await addDoc(collection(db, 'organizations', orgId, 'nearMiss', id, 'comments'), {
        author: userData?.name || userData?.email || 'Unknown',
        authorId: userData?.uid || 'unknown',
        text: newComment.trim(),
        createdAt: new Date().toISOString()
      })
      setNewComment('')
    } catch (err: any) {
      alert(err?.message || 'Failed to add comment.')
    } finally {
      setAddingComment(false)
    }
  }

  const handleEmail = () => {
    if (!item) return
    const subject = encodeURIComponent(`Near Miss/Good Catch: ${item.title}`)
    const body = encodeURIComponent(
      `Title: ${item.title}\nCategory: ${item.category}\nWork Area: ${item.workArea}\nSeverity: ${item.severity}\nStatus: ${item.status}\nDescription: ${item.description || 'N/A'}\n\nView in SiteDeck Pro: ${window.location.origin}/near-miss/${id}`
    )
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
  }

  const currentStatusIdx = item ? STATUS_ORDER.indexOf(item.status) : 0

  if (loading) {
    return (
      <AppLayout title="Near Miss / Good Catch">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      </AppLayout>
    )
  }

  if (!item) {
    return (
      <AppLayout title="Near Miss / Good Catch">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <p className="text-lg font-medium">Entry not found</p>
          <button onClick={() => navigate('/near-miss')} className="mt-4 text-blue-600 hover:underline text-sm">
            ← Back to Near Miss / Good Catch
          </button>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Near Miss / Good Catch">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/near-miss')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          ← Back to Near Miss / Good Catch
        </button>
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          ✉ Send by Email
        </button>
      </div>

      <div className="max-w-4xl flex gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex-1">{item.title}</h1>
            <span
              className="px-3 py-1 rounded text-xs font-semibold text-white"
              style={{ backgroundColor: SEVERITY_COLORS[item.severity] || '#888' }}
            >
              {item.severity || 'Low'}
            </span>
            <span
              className="px-3 py-1 rounded text-xs font-semibold"
              style={{ backgroundColor: (STATUS_COLORS[item.status] || '#888') + '20', color: STATUS_COLORS[item.status] || '#888' }}
            >
              {item.status || 'Open'}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#9333ea' }} />
              {item.category || 'General'}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#0891b2' }} />
              {item.workArea || 'General'}
            </span>
            <span>•</span>
            <span>{formatDate(item.createdAt)}</span>
          </div>

          {item.description && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Description</h3>
              <p className="text-gray-700 leading-relaxed">{item.description}</p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Created By</span>
              <span className="text-gray-800 font-medium">{item.createdBy || 'Unknown'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Project</span>
              <span className="text-gray-800 font-medium">{item.project || '-'}</span>
            </div>
            {item.notes && (
              <div className="flex flex-col gap-1 pt-2 border-t border-gray-100">
                <span className="text-gray-500 text-sm">Notes</span>
                <span className="text-gray-800 text-sm">{item.notes}</span>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Update Status</h3>
            <div className="flex items-center gap-2 mb-4">
              {STATUS_ORDER.map((st, idx) => {
                const isDone = idx < currentStatusIdx
                const isCurrent = idx === currentStatusIdx
                const isNext = idx === currentStatusIdx + 1
                const canClick = isNext

                return (
                  <React.Fragment key={st}>
                    <button
                      onClick={() => {
                        if (!canClick) return
                        if (st === 'Reviewed') setShowReviewNotes(true)
                        else if (st === 'Closed') setShowCloseNotes(true)
                        else handleStatusUpdate(st)
                      }}
                      disabled={!canClick || statusUpdating}
                      className={`flex-1 py-2.5 rounded-lg font-medium text-sm border-2 transition-all ${
                        isDone ? 'text-white' : isCurrent ? 'text-white' : 'opacity-40'
                      }`}
                      style={{
                        borderColor: STATUS_COLORS[st],
                        backgroundColor: isDone || isCurrent ? STATUS_COLORS[st] : 'transparent',
                        color: isDone || isCurrent ? '#fff' : STATUS_COLORS[st],
                        cursor: canClick ? 'pointer' : 'default'
                      }}
                    >
                      {st}
                    </button>
                    {idx < STATUS_ORDER.length - 1 && (
                      <span className="text-gray-300 text-lg">→</span>
                    )}
                  </React.Fragment>
                )
              })}
            </div>

            <p className="text-xs text-gray-400 mb-4">
              {currentStatusIdx === STATUS_ORDER.length - 1
                ? 'Entry is fully closed.'
                : `Next step: ${STATUS_ORDER[currentStatusIdx + 1]}`}
            </p>

            {showReviewNotes && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Review Notes *</label>
                <textarea
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm"
                  rows={3}
                  placeholder="Enter review notes before marking as Reviewed..."
                />
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => { setShowReviewNotes(false); setReviewNotes('') }}
                    className="px-5 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('Reviewed')}
                    disabled={statusUpdating || !reviewNotes.trim()}
                    className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    {statusUpdating ? 'Saving...' : 'Save & Mark Reviewed'}
                  </button>
                </div>
              </div>
            )}

            {showCloseNotes && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Close Out Notes *</label>
                <textarea
                  value={closeNotes}
                  onChange={e => setCloseNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm"
                  rows={3}
                  placeholder="Enter close out notes before marking as Closed..."
                />
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => { setShowCloseNotes(false); setCloseNotes('') }}
                    className="px-5 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('Closed')}
                    disabled={statusUpdating || !closeNotes.trim()}
                    className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300"
                  >
                    {statusUpdating ? 'Saving...' : 'Save & Mark Closed'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="w-80 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 flex flex-col" style={{ height: '600px' }}>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Comments & Activity</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {comments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center pt-8">No comments yet. Add the first note below.</p>
              ) : (
                comments.map(c => (
                  <div key={c.id} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">{c.author}</span>
                      <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-lg px-3 py-2">{c.text}</p>
                  </div>
                ))
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 space-y-3">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Add a comment or note..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                rows={3}
              />
              <button
                onClick={handleAddComment}
                disabled={addingComment || !newComment.trim()}
                className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
              >
                {addingComment ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
